
import React from "react";
import { useReportsData } from "@/hooks/reports";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  ZAxis
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { TimeRangeSelector } from "./TimeRangeSelector";

export function TailorProductivityReport() {
  const { loading, getTailorProductivity, timeRange, setTimeRange } = useReportsData();
  
  const tailorData = getTailorProductivity();
  
  // Calculate overall metrics
  const totalCompleted = tailorData.reduce((sum, tailor) => sum + tailor.completedOrders, 0);
  const totalOrders = tailorData.reduce((sum, tailor) => sum + tailor.totalOrders, 0);
  const overallProductivity = totalOrders > 0 ? Math.round((totalCompleted / totalOrders) * 100) : 0;
  
  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Tailor Productivity Analysis</h3>
        <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Overall Productivity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{overallProductivity}%</div>
            <p className="text-sm text-muted-foreground">Average completion rate</p>
          </CardContent>
        </Card>
        
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Most Productive Tailor</CardTitle>
          </CardHeader>
          <CardContent>
            {tailorData.length > 0 ? (
              <>
                <div className="text-2xl font-bold">{tailorData[0]?.name || "N/A"}</div>
                <p className="text-sm text-muted-foreground">
                  {tailorData[0]?.productivity || 0}% productivity rate
                </p>
              </>
            ) : (
              <p>No tailor data available</p>
            )}
          </CardContent>
        </Card>
        
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Most Active Tailor</CardTitle>
          </CardHeader>
          <CardContent>
            {tailorData.length > 0 ? (
              <>
                <div className="text-2xl font-bold">
                  {tailorData[0]?.name || "N/A"}
                </div>
                <p className="text-sm text-muted-foreground">
                  {tailorData[0]?.totalOrders || 0} total orders
                </p>
              </>
            ) : (
              <p>No tailor data available</p>
            )}
          </CardContent>
        </Card>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Productivity by Tailor</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <p>Loading data...</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={tailorData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value}%`, 'Productivity']} />
                  <Legend />
                  <Bar dataKey="productivity" name="Productivity %" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
        
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Orders Count by Tailor</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <p>Loading data...</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={tailorData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="completedOrders" name="Completed" stackId="a" fill="#4caf50" />
                  <Bar dataKey="inProgressOrders" name="In Progress" stackId="a" fill="#2196f3" />
                  <Bar dataKey="pendingOrders" name="Pending" stackId="a" fill="#ff9800" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
      
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Tailor Performance Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-8">
            {tailorData.map((tailor) => (
              <div key={tailor.id} className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">{tailor.name}</h4>
                  <span className="text-sm font-medium">{tailor.productivity}% Productivity</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{tailor.completedOrders} completed</span>
                  <span>•</span>
                  <span>{tailor.inProgressOrders} in progress</span>
                  <span>•</span>
                  <span>{tailor.pendingOrders} pending</span>
                  <span>•</span>
                  <span>{tailor.totalOrders} total orders</span>
                </div>
                <Progress value={tailor.productivity} className="h-2" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
